/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { State } from '@core/store';
import * as DepartmentsActions from '@departments/store/departments.actions';
import { getDriverDepartments } from '@departments/store/departments.selectors';
import * as DriversActions from '@drivers/store/drivers.actions';
import { getCurrentDriver, getIsDriverActionPending } from '@drivers/store/drivers.selectors';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Department, Driver, LicenseClass } from '@shared/models';
import { invalidFieldsHint } from '@shared/utils';
import { getLicenseClasses } from '@vehicles/store/vehicles.selectors';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

/** Formularfeld zu Beschriftung - fuer den Hinweis am gesperrten Speichern-Button. */
const FIELD_LABELS: Record<string, string> = {
  firstName: 'user.firstName',
  lastName: 'user.lastName',
  abbreviation: 'user.abbreviation',
  licenseClass: 'vehicles.licenseClass',
  department: 'departments.department'
};

@Component({
    selector: 'app-driver-detail',
    templateUrl: './driver-detail.component.html',
    styleUrls: ['./driver-detail.component.scss'],
    standalone: false
})
export class DriverDetailComponent implements OnInit {
  driver$: Observable<Driver>;
  isActionPending: Observable<boolean>;
  licenseClasses$: Observable<LicenseClass[]>;
  departments$: Observable<Department[]>;

  driverForm = new UntypedFormGroup({
    firstName: new UntypedFormControl('', Validators.required),
    lastName: new UntypedFormControl('', Validators.required),
    abbreviation: new UntypedFormControl('', Validators.required),
    licenseClass: new UntypedFormControl('', Validators.required),
    department: new UntypedFormControl('', Validators.required)
  });

  constructor(
    private store: Store<State>,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public parameters: { driverId: string }
  ) { }

  /** Erklaert am Tooltip, warum der Speichern-Button gesperrt ist. */
  get persistHint(): string {
    return invalidFieldsHint(
      this.driverForm,
      FIELD_LABELS,
      (key, params) => this.translateService.instant(key, params)
    );
  }

  ngOnInit(): void {
    this.store.dispatch(new DriversActions.LoadDriver(this.parameters.driverId));
    this.store.dispatch(new DepartmentsActions.LoadDepartments());

    this.isActionPending = this.store.select(getIsDriverActionPending);
    this.licenseClasses$ = this.store.select(getLicenseClasses);
    this.departments$ = this.store.select(getDriverDepartments);
    this.driver$ = this.store.select(getCurrentDriver).pipe(
      // Die Abteilung kommt als Objekt vom Backend, das Auswahlfeld arbeitet aber mit
      // der IRI - genau die wird beim Speichern auch wieder gesendet.
      tap(driver => this.driverForm.patchValue({
        ...driver,
        department: driver.department ? driver.department['@id'] : null
      }))
    );
  }

  persistDriver(driver: Driver): void {
    // Der Button ist zwar "disabled", bleibt wegen disabledInteractive (fuer den Tooltip)
    // aber klickbar - Material unterdrueckt Klicks nur bei Anchor-Elementen.
    if (this.driverForm.invalid) {
      return;
    }

    this.store.dispatch(new DriversActions.PersistDriver({
      ...driver,
      ...this.driverForm.value
    }));
  }
}
